import React, { useState, useEffect } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { getStripeSku } from '../../const/Pay';
import { createSubscriptionRequest } from '../../utils/api';
import { PAYMENT_TYPE_STRIPE } from '../../const/Pay';
import WalletPayButton from './WalletPayButton';
import { mjDebugLog, mjDebugError } from '../../utils/logger';
import { User } from '../../domin/pay/User';
import { SKU_INFO } from '../../const/LocalStorage';
import { buildLocalizedPath } from '../../utils/common';

// Load Stripe outside of component to avoid recreating on every render
const stripePromise = loadStripe(getStripeSku());

/**
 * WalletPayWrapper Component
 * Wraps WalletPayButton with Stripe Elements provider
 * Handles subscription creation and payment confirmation
 * 
 * @param {Object} props
 * @param {Object} props.price - Price object containing SKU and pricing information
 * @param {number} props.discount - Discount percentage (e.g., 71 for 71% off)
 * @param {Object} props.weightLossFormData - User form data (including email)
 * @param {Function} props.onPaymentSuccess - Callback when payment succeeds
 * @param {Function} props.onPaymentError - Callback when payment fails
 * @param {Function} props.onWalletAvailabilityChange - Callback when wallet availability is checked
 * @param {Function} props.logEvent - Event logging function
 */
const WalletPayWrapper = ({ 
  price, 
  discount,
  weightLossFormData,
  onPaymentSuccess, 
  onPaymentError,
  onWalletAvailabilityChange,
  logEvent = () => {}
}) => {
  const [clientSecret, setClientSecret] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isWalletAvailable, setIsWalletAvailable] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(true);
  const [walletPaymentTypes, setWalletPaymentTypes] = useState({ applePay: false, googlePay: false });

  // Check wallet payment availability on mount
  useEffect(() => {
    checkWalletAvailability();
  }, []);

  /**
   * Check if wallet payment (Apple Pay or Google Pay) is available
   * Similar to checkWalletPay in jQuery implementation
   */
  const checkWalletAvailability = async () => {
    try {
      const stripe = await stripePromise;
      if (!stripe) {
        mjDebugError('Stripe not loaded for availability check', {});
        setCheckingAvailability(false);
        return;
      }

      const user = new User();
      const paymentRequest = stripe.paymentRequest({
        country: "US",//user.getCountryCode(),
        currency: user.getCurrencyCode(),
        total: {
          label: 'Calo',
          amount: Math.round(1 * 100), // Dummy amount for checking availability
        },
      });

      const result = await paymentRequest.canMakePayment();
      
      if (result && (result.applePay || result.googlePay)) {
        // Wallet payment is available
        setIsWalletAvailable(true);
        const walletTypes = {
          applePay: !!result.applePay,
          googlePay: !!result.googlePay
        };
        setWalletPaymentTypes(walletTypes);
        
        // Notify parent component about wallet availability
        if (onWalletAvailabilityChange) {
          onWalletAvailabilityChange(walletTypes);
        }
        
        mjDebugLog('Wallet pay available', {
          applePay: result.applePay,
          googlePay: result.googlePay
        });
        
        logEvent('checkwalletpaysuccess_exposure', {
          type: result.applePay ? 'apple_pay' : 'google_pay'
        }, 'pay');
      } else {
        // No wallet payment available
        setIsWalletAvailable(false);
        const walletTypes = {
          applePay: false,
          googlePay: false
        };
        setWalletPaymentTypes(walletTypes);
        
        // Notify parent component about wallet availability
        if (onWalletAvailabilityChange) {
          onWalletAvailabilityChange(walletTypes);
        }
        
        mjDebugLog('Wallet pay not available', { result });
        
        logEvent('checkwalletpayerror_exposure', {
          error: 'Wallet Pay is not available on this device'
        }, 'pay');
      }
    } catch (error) {
      mjDebugError('Error checking wallet availability', {
        error: error.message
      });
      
      logEvent('checkwalletpayerror_exposure', {
        error: error.message
      }, 'pay');
      
      // Notify parent component that wallet is not available
      if (onWalletAvailabilityChange) {
        onWalletAvailabilityChange({ applePay: false, googlePay: false });
      }
      
      setIsWalletAvailable(false);
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleWalletRenderStateChange = (visible) => {
    if (!visible) {
      setIsWalletAvailable(false);
      setWalletPaymentTypes({ applePay: false, googlePay: false });
      if (onWalletAvailabilityChange) {
        onWalletAvailabilityChange({ applePay: false, googlePay: false });
      }
    }
  };
  
  const handleWalletPaymentClick = async ({ paymentType, isWallet }) => {
    if (loading) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      mjDebugLog('Creating wallet payment subscription', {
        sku: price?.sku?.stripe,
        isWallet
      });

      const [newSessionId, newClientSecret] = await createSubscriptionRequest(
        price?.sku?.stripe,
        weightLossFormData.email,
        price.getDiscountValueForServer(),
        price.dynamicCycleSubscription,
        paymentType
      );

      if (!newClientSecret) {
        throw new Error('Failed to get client secret from server');
      }

      setClientSecret(newClientSecret);
      setSessionId(newSessionId);

      // Save SKU info to localStorage
      localStorage.setItem(SKU_INFO, JSON.stringify({
        sku: price?.sku?.stripe,
        sku_id: price?.getUniqueId() || "",
        session_id: newSessionId,
        payment_type: paymentType,
        discount: discount, // discount percentage
        price: price?.getPrice() || "",
        week: price?.week
      }));

      mjDebugLog('Wallet payment subscription created', {
        sessionId: newSessionId
      });

      logEvent('createsuccess_exposure', {
        type: paymentType + "",
        sku: price?.sku?.stripe,
        percent: discount, // discount percentage
        string2: price?.getUniqueId() || "", // SKU ID
        string1: price?.getPrice(),
        string3: price?.week + ""
      }, 'pay', true); // instantly = true

      // Confirm payment with Stripe
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe not loaded');
      }

      // Get current language path for return URL
      const returnUrl = `${window.location.origin}${buildLocalizedPath('/pay/verify')}`;

      mjDebugLog('Confirming wallet payment', {
        returnUrl
      });

      const { error: confirmError } = await stripe.confirmPayment({
        clientSecret: newClientSecret,
        redirect: 'if_required',
        confirmParams: {
          return_url: returnUrl,
        },
      });

      if (confirmError) {
        throw confirmError;
      }

      // Payment succeeded
      mjDebugLog('Wallet payment confirmed', {
        sessionId: newSessionId
      });

      logEvent('expresscheckoutsuccess_exposure', {}, 'pay');

      if (onPaymentSuccess) {
        onPaymentSuccess({
          sessionId: newSessionId,
          paymentType
        });
      }

    } catch (err) {
      mjDebugError('Wallet payment failed', {
        error: err.message
      });

      logEvent('createfail_show', {
        error: err.message,
        type: PAYMENT_TYPE_STRIPE + ''
      }, 'pay');

      setError(err.message);

      if (onPaymentError) {
        onPaymentError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!price || !weightLossFormData?.email) {
    return null;
  }

  // Don't render if checking availability
  if (checkingAvailability) {
    return (
      <div className="wallet-pay-wrapper" style={{ minHeight: '55px' }}>
        <div className="wallet-pay-loading">
          <div className="wallet-pay-spinner"></div>
        </div>
      </div>
    );
  }

  // Don't render if wallet pay is not available
  if (!isWalletAvailable) {
    return null;
  }

  // For Express Checkout, we don't need clientSecret upfront
  // Stripe will handle the payment flow internally
  const options = {
    mode: 'payment',
    amount: Math.round(price.price * 100), // Amount in cents
    currency: price.currency || 'usd',
    appearance: {
      variables: {
        borderRadius: '16px',
      },
    },
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <div className="wallet-pay-wrapper">
        {error && (
          <div className="wallet-pay-error" style={{ 
            color: 'red', 
            marginBottom: '10px',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}
        
        <WalletPayButton
          price={price}
          sessionId={sessionId}
          onPaymentSuccess={handleWalletPaymentClick}
          onPaymentError={onPaymentError}
          onRenderStateChange={handleWalletRenderStateChange}
          logEvent={logEvent}
        />
        
        {loading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10
          }}>
            <div style={{ fontSize: '14px', color: '#666' }}>
              Processing...
            </div>
          </div>
        )}
      </div>
    </Elements>
  );
};

export default WalletPayWrapper;
